import type { PickerSource } from 'slices/aiConversations/pickerTypes'

import type {
  PickerRowsArgs,
  PickerRowsByKeyArgs,
  PickerRowsByUidArgs,
  PickerRowsPage,
  ScopedUidGroupsArgs,
  SnowflakeRowsWire,
  StoreUidRow,
  StoreUidsArgs,
  UidGroupRow,
  UidGroupsOfProductsArgs,
  UidsLookup,
} from '../canonicalPicker.types'
import { api } from '../index'
import { allRowsOf } from './paging'
import {
  fieldsFor,
  filterConditions,
  orderFor,
  PAGE_SIZE,
  queryUrl,
  rowsByKeyUrls,
  scopeConditions,
  searchConditions,
  storeUidsUrls,
} from './query'
import { pageOf, storeUidRowsOf } from './rows'
import scopedUidGroups from './scopedUidGroups'
import uidGroupsOfProducts from './uidGroupsOfProducts'
import uidsLookup from './uidsLookup'

export {
  canListStoreUids,
  isScopedSource,
  KEYS_PER_REQUEST,
  MAX_PAGE_SIZE,
  PAGE_SIZE,
  PRODUCTS_PER_REQUEST,
  rowsByKeyUrls,
  snowflakeQueryString,
  UIDS_PER_REQUEST,
  uidLookupUrl,
} from './query'
export { PARALLEL_PAGES, PARALLEL_QUERIES } from './paging'
export { groupReaching, uidGroupRowId } from './rows'

/**
 * Where a smart source's products keep their variants: on its own rows, or in
 * the table its variants_lookup names, read by the same product id. null when
 * neither, so the level has no versions to offer.
 */
export const variantsSourceOf = (source: PickerSource): PickerSource | null => {
  if (source.variantsField) return source
  const lookup = source.variantsLookup
  if (!lookup) return null
  return {
    ...source,
    keyField: lookup.key_field,
    // The lookup names no name column, so fieldsFor asks for the key once and a pick keeps the name it was listed with
    valueField: lookup.key_field,
    // Unscoped on purpose: a smart item targets all its versions whatever modifier level 1 holds, and the table has no modifier column
    params: { table_name: lookup.table_name },
    variantsField: lookup.variants_field,
    variantsLookup: null,
    detailsSource: null,
  }
}

export const canonicalPickerApi = api.injectEndpoints({
  endpoints: builder => ({
    getPickerRows: builder.query<PickerRowsPage, PickerRowsArgs>({
      query: ({ source, kind, search, page, scope }) =>
        queryUrl(source, {
          fields: fieldsFor(source, kind, false),
          distinct: source.params.distinct === true,
          where: [
            ...filterConditions(source),
            ...scopeConditions(source, scope),
            ...searchConditions(source, search),
          ],
          orderBy: orderFor(source, kind),
          page,
          perPage: PAGE_SIZE,
        }),
      transformResponse: (
        response: SnowflakeRowsWire,
        _meta,
        { source, kind }
      ) => pageOf(response, source, kind, false),
    }),
    getPickerRowsByKey: builder.query<PickerRowsPage, PickerRowsByKeyArgs>({
      async queryFn(args, _api, _extraOptions, baseQuery) {
        const result = await allRowsOf(rowsByKeyUrls(args), baseQuery)
        if ('error' in result) return result
        return { data: pageOf(result.data, args.source, args.kind, true) }
      },
    }),
    getUidGroupsByProduct: builder.query<
      PickerRowsPage,
      UidGroupsOfProductsArgs
    >({
      queryFn: (args, _api, _extraOptions, baseQuery) =>
        uidGroupsOfProducts(args, baseQuery),
    }),
    getPickerRowsByUid: builder.query<UidsLookup, PickerRowsByUidArgs>({
      queryFn: (args, _api, _extraOptions, baseQuery) =>
        uidsLookup(args, baseQuery),
    }),
    getScopedUidGroups: builder.query<UidGroupRow[], ScopedUidGroupsArgs>({
      queryFn: (args, _api, _extraOptions, baseQuery) =>
        scopedUidGroups(args, baseQuery),
    }),
    getStoreUids: builder.query<StoreUidRow[], StoreUidsArgs>({
      async queryFn({ source, group }, _api, _extraOptions, baseQuery) {
        const result = await allRowsOf(
          storeUidsUrls(source, group.provider, group.storeUids ?? []),
          baseQuery
        )
        if ('error' in result) return result
        return { data: storeUidRowsOf(result.data, group) }
      },
    }),
  }),
})

export const {
  useGetPickerRowsQuery,
  useGetScopedUidGroupsQuery,
  useGetStoreUidsQuery,
  useLazyGetPickerRowsByKeyQuery,
  useGetPickerRowsByUidQuery,
  useLazyGetUidGroupsByProductQuery,
} = canonicalPickerApi
